import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';
import Notice from '../Notice/Notice';

/**
 * `RadioButton` is an extension of the HTML `input` element type "radio" and is used when only one item of a set can be selected at any given time. The component accepts all properties supported by the standard radio button element.
 *
 * An optional {@link Notice} can be displayed below the radio button.
 *
 * @property {boolean} checked If set to `true`, this radio button will be checked. Defaults to `false`.
 * @property {node} children Custom children that will be inserted into the container that wraps the radio button HTML
 * element.
 * @property {string} className The class name of the component. Will be appended to "core-radiobutton".
 * @property {boolean} disabled The user cannot change the state of this radio button when this prop is set to `true`.
 * @property {node} errorMessage An error message that will be shown below the input field. Defaults to no error message.
 * @property {node} successMessage A message that will be shown below the input field. Defaults to no message.
 * @property {node} infoMessage A message that will be shown below the input field. Defaults to no message.
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 * @property {string} id The ID of the radio button element.
 * @property {string} label The label of this component.
 * @property {function} onChange The callback function that will be called whenever the user has changed the state of this component.
 *
 * @see Checkbox, Notice, Switch
 */
export default class RadioButton extends PureComponent {

  static displayName = 'RadioButton';

  static propTypes = {
    checked: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    errorMessage: PropTypes.node,
    id: PropTypes.string,
    infoMessage: PropTypes.node,
    label: PropTypes.string,
    successMessage: PropTypes.node,
    warningMessage: PropTypes.node,
  };

  render() {
    const { children, className, errorMessage, id, infoMessage, label, notice, noticeType, successMessage, warningMessage, ...rest } = this.props;
    return (
      <span className={cx(
        'core-radiobutton',
        className, { checked: rest.checked, disabled: rest.disabled, 'has-errors': errorMessage || warningMessage || successMessage || infoMessage })}>
        <label htmlFor={id}>
          <input {...rest} id={id} type="radio"/>
          <Icon name={rest.checked ? 'radiobutton-checked' : 'radiobutton-unchecked'} className="icon"/>
          {label && <span className="title">{label}</span>}
        </label>
        {children}
        {(errorMessage || warningMessage || successMessage || infoMessage) && <Notice errorMessage={errorMessage} warningMessage={warningMessage} successMessage={successMessage} infoMessage={infoMessage}/>}
      </span>
    );
  }

}
